import { left, right } from '@sweet-monads/either';
import type { Either } from '@sweet-monads/either';

import { AbstractError } from '@/domain/errors/abstract-error.js';
import { ServerError } from '@/domain/errors/server-error.js';
import type {
    ClientBtpEnvResponse,
    ClientDetailModel,
    ClientDocResponse,
    ClientErpEnvResponse,
    ClientModel,
    CreateBtpEnvParams,
    CreateClientParams,
    CreateErpEnvParams,
    UpdateBtpEnvParams,
    UpdateClientParams,
    UpdateErpEnvParams,
    UploadClientDocumentParams
} from '@/domain/models/client.js';
import type { ClientRepository } from '@/domain/repositories/client-repository.js';
import type {
    CreateClient,
    CreateClientBtpEnv,
    CreateClientErpEnv,
    DeleteClientBtpEnv,
    DeleteClientDocument,
    DeleteClientErpEnv,
    GetClientDocumentDownloadUrl,
    ListClientBtpEnvs,
    ListClientDocuments,
    ListClientErpEnvs,
    LoadClientById,
    LoadClients,
    RemoveClientLogo,
    UpdateClient,
    UpdateClientBtpEnv,
    UpdateClientErpEnv,
    UploadClientDocument,
    UploadClientLogo
} from '@/domain/use-cases/clients.js';

export class LoadClientsUseCase implements LoadClients {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(params?: { search?: string }): Promise<Either<AbstractError, ClientModel[]>> {
        try {
            return right(await this.repository.findAll(params?.search));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadClientByIdUseCase implements LoadClientById {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, ClientDetailModel>> {
        try {
            return right(await this.repository.findById(id));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateClientUseCase implements CreateClient {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(params: CreateClientParams): Promise<Either<AbstractError, ClientModel>> {
        try {
            return right(await this.repository.create(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateClientUseCase implements UpdateClient {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(id: string, params: UpdateClientParams): Promise<Either<AbstractError, ClientModel>> {
        try {
            return right(await this.repository.update(id, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UploadClientLogoUseCase implements UploadClientLogo {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string, params: { fileData: string; fileName: string; mimeType: string }): Promise<Either<AbstractError, { logoUrl: string }>> {
        try {
            return right(await this.repository.uploadLogo(clientId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RemoveClientLogoUseCase implements RemoveClientLogo {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.removeLogo(clientId);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListClientErpEnvsUseCase implements ListClientErpEnvs {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string): Promise<Either<AbstractError, ClientErpEnvResponse[]>> {
        try {
            return right(await this.repository.listErpEnvironments(clientId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateClientErpEnvUseCase implements CreateClientErpEnv {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string, params: CreateErpEnvParams): Promise<Either<AbstractError, ClientErpEnvResponse>> {
        try {
            return right(await this.repository.createErpEnvironment(clientId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateClientErpEnvUseCase implements UpdateClientErpEnv {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(envId: string, params: UpdateErpEnvParams): Promise<Either<AbstractError, ClientErpEnvResponse>> {
        try {
            return right(await this.repository.updateErpEnvironment(envId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DeleteClientErpEnvUseCase implements DeleteClientErpEnv {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(envId: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.deleteErpEnvironment(envId);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListClientBtpEnvsUseCase implements ListClientBtpEnvs {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string): Promise<Either<AbstractError, ClientBtpEnvResponse[]>> {
        try {
            return right(await this.repository.listBtpEnvironments(clientId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateClientBtpEnvUseCase implements CreateClientBtpEnv {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string, params: CreateBtpEnvParams): Promise<Either<AbstractError, ClientBtpEnvResponse>> {
        try {
            return right(await this.repository.createBtpEnvironment(clientId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateClientBtpEnvUseCase implements UpdateClientBtpEnv {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(envId: string, params: UpdateBtpEnvParams): Promise<Either<AbstractError, ClientBtpEnvResponse>> {
        try {
            return right(await this.repository.updateBtpEnvironment(envId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DeleteClientBtpEnvUseCase implements DeleteClientBtpEnv {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(envId: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.deleteBtpEnvironment(envId);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListClientDocumentsUseCase implements ListClientDocuments {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string): Promise<Either<AbstractError, ClientDocResponse[]>> {
        try {
            return right(await this.repository.listDocuments(clientId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UploadClientDocumentUseCase implements UploadClientDocument {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string, params: UploadClientDocumentParams): Promise<Either<AbstractError, ClientDocResponse>> {
        try {
            return right(await this.repository.uploadDocument(clientId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DeleteClientDocumentUseCase implements DeleteClientDocument {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string, docId: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.deleteDocument(clientId, docId);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class GetClientDocumentDownloadUrlUseCase implements GetClientDocumentDownloadUrl {
    constructor(private readonly repository: ClientRepository) {}

    public async execute(clientId: string, docId: string): Promise<Either<AbstractError, { url: string }>> {
        try {
            return right(await this.repository.getDocumentDownloadUrl(clientId, docId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}
